<!-- Copyright (c) 2019-2022 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<script>
import {
  fromPairs, isEmpty, map, last, has, noop,
} from 'lodash';
import {
  CallbackType, FRWebAuthn, WebAuthnStepType,
} from '@forgerock/javascript-sdk';
import createRealmPath from '../../utils/createRealmPath';
import NotificationMixin from '../NotificationMixin';

export const FrCallbackType = {
  ...CallbackType,
  RecoveryCodesComponent: 'RecoveryCodesComponent',
  RedirectCallback: 'RedirectCallback',
  SelectIdPCallback: 'SelectIdPCallback',
  SuspendedTextOutputCallback: 'SuspendedTextOutputCallback',
  WebAuthnComponent: 'WebAuthnComponent',
};

export function getIdFromSession() {
  return this.getRequestService({
    context: 'AM',
    apiVersion: 'protocol=1.0,resource=2.0',
  }).post('/users?_action=idFromSession', {}, { withCredentials: true });
}

export function getUserInfo(session) {
  const urlParams = new URLSearchParams(window.location.search);
  const realm = urlParams.get('realm');
  const realmPath = createRealmPath(realm);
  const path = realmPath.length !== 0 ? `/realms/root/${realmPath}/users/${session.data.id}` : `/users/${session.data.id}`;

  return this.getRequestService({
    context: 'AM',
    apiVersion: 'protocol=1.0,resource=2.0',
  }).get(path, { withCredentials: true });
}

export function getConfigurationInfo(realm) {
  const realmPath = createRealmPath(realm);
  const requestPath = realmPath.length !== 0 ? `/realms/root/${realmPath}/serverinfo/*` : '/serverinfo/*';

  return this.getRequestService({
    context: 'AM',
    apiVersion: 'protocol=1.0,resource=1.1',
  }).get(requestPath, { withCredentials: true, suppressEvents: true });
}
// isDefaultPath is looking for a path that looks like '/am/console' or '/auth/console'
export function isDefaultPath(path) {
  return last(path.split('/')) === 'console';
}

export function isSamlURL(url) {
  return url.includes('/Consumer/metaAlias') || url.includes('/saml2');
}

/**
  * @param {string} url - current url after successful login
  * If the url contains a 'goto' query (i.e. ?goto=www.test.com)
  * then validate the goto url.
  *
  * If it has been registered in AM, redirect the user to the url
  * Else redirect to the default login url.
  */
export function verifyGotoUrlAndRedirect(url, realm, isAdmin = false, isGotoOnFail = false) {
  const urlParams = new URLSearchParams(window.location.search);
  let gotoUrl;

  // This aligns the goto parameter order to match what was used in AM and what is documented in the Docs
  const alignGotoPrecedence = process.env.VUE_APP_ALIGN_GOTO_PRECEDENCE;
  if (alignGotoPrecedence === 'true') {
    gotoUrl = !isGotoOnFail
      ? JSON.stringify({ goto: url })
      : JSON.stringify({ goto: urlParams.get('gotoOnFail') });
  } else {
    gotoUrl = !isGotoOnFail
      ? JSON.stringify({ goto: urlParams.get('goto') || url })
      : JSON.stringify({ goto: urlParams.get('gotoOnFail') });
  }

  urlParams.delete('goto');
  urlParams.delete('gotoOnFail');
  urlParams.delete('realm');

  const ampersand = urlParams.toString().length ? '&' : '';
  const paramsToString = urlParams.toString().length ? `${urlParams.toString()}` : '';
  const redirectUserBasedOnType = () => {
    if (isAdmin) {
    // admin user
      return `${process.env.VUE_APP_ADMIN_URL}?realm=${realm}${ampersand}${paramsToString}`;
    }
    // end user
    return `${process.env.VUE_APP_ENDUSER_URL}?realm=${realm}${ampersand}${paramsToString}`;
  };

  return this.getRequestService({
    context: 'AM',
    apiVersion: 'protocol=2.1,resource=3.0',
  })
    .post('/users?_action=validateGoto', gotoUrl, { withCredentials: true })
    .then((res) => {
      if (!isDefaultPath(res.data.successURL) && res.data.successURL !== 'undefined' && res.data.successURL !== null) {
        return res.data.successURL;
      }
      if (isDefaultPath(res.data.successURL) && isGotoOnFail) {
        return false;
      }
      if (isDefaultPath(res.data.successURL) && !isDefaultPath(url)) {
        return url;
      }
      if (isDefaultPath(res.data.successURL) && isSamlURL(JSON.parse(gotoUrl).goto)) {
        return JSON.parse(gotoUrl).goto;
      }
      return redirectUserBasedOnType();
    })
    .catch(() => redirectUserBasedOnType());
}

/**
 * Returns the query string from the URI
 * @returns {string} Unescaped query string or empty string if no query string was found
 */
export function getCurrentQueryString() {
  const queryString = window.location.search;

  return queryString.substr(1, queryString.length);
}

/**
 * @description Creates an object of key value pairs from the passed in query string
 * @param {string} paramString A string containing a query string
 * @returns {object} An Object of key value pairs
 */
export function parseParameters(paramString) {
  const object = isEmpty(paramString) ? {} : fromPairs(map(paramString.split('&'), (pair) => pair.split('=')));
  return object;
}

/**
 * @description Determines if passed in callback is set to required or has a required policy
 * @param {Object} callback - callback to check if required
 * @returns {Boolean} True if passed in callback is required
 */
export function isCallbackRequired(callback) {
  const requiredOutput = callback.getOutputByName('required');
  if (requiredOutput === true && callback.getType() !== 'BooleanAttributeInputCallback') {
    return true;
  }
  const policyOutput = callback.getOutputByName('policies');
  if (has(policyOutput, 'policies')) {
    const requiredPolicy = policyOutput.policies.find((policy) => policy.policyId === 'required');
    if (has(requiredPolicy, 'policyRequirements') && requiredPolicy.policyRequirements.includes('REQUIRED')) {
      return true;
    }
  }
  return false;
}

/**
 * @description provides translated policy errors for a callback
 * @param {Object} callback - callback to translate policy validation errors for
 * @returns {Array} an array of translated policty validation error messages
 */
export function getTranslatedPolicyFailures(callback) {
  const failedPolicies = callback.getFailedPolicies
    ? callback.getFailedPolicies()
    : [];
  return failedPolicies.map((policy) => {
    const parsedPolicy = JSON.parse(policy);
    return this.$t(`common.policyValidationMessages.${parsedPolicy.policyRequirement}`, parsedPolicy.params);
  });
}

/**
 * @description gets field information to show when rendering login forms
 * @param {Object} callback specific step callback
 * @param {Object} index callback index
 * @returns {Object} field props needed for Field component
 */
export function getField(callback, index) {
  const callbackType = callback.getType();
  let fieldType;

  switch (callbackType) {
    case FrCallbackType.PasswordCallback:
    case FrCallbackType.ValidatedCreatePasswordCallback:
      fieldType = 'password';
      break;
    case FrCallbackType.NumberAttributeInputCallback:
      fieldType = 'number';
      break;
    default:
      fieldType = 'string';
      break;
  }

  let label = '';
  if (callback.getPrompt) {
    label = callback.getPrompt();
  } else if (callback.getOutputByName) {
    try {
      label = callback.getOutputByName('prompt').value;
    } catch (e) {
      noop();
    }
  }
  return {
    label,
    fieldType,
    name: `callback_${index}`,
    value: callback.getInputValue(),
  };
}

/**
 * @description determine if any backend scripts in the given SDK step have id's which contain a passed string
 * @param {String} matcher the string to check against script ids
 * @param {Object} step the SDK step to check within
 * @returns {Boolean} whether any backend scripts within the given step contain the passed matcher string
 */
export function backendScriptsIdsContains(matcher, step) {
  const typeArr = step.getCallbacksOfType(FrCallbackType.HiddenValueCallback)
    .map((callback) => callback.getOutputByName('id', ''));
  return typeArr.indexOf(matcher) >= 0;
}

/**
 * @description  Invokes WebAuthn registration or authentication
 * @param {Number} type enum number that represents WebAuthn type WebAuthnStepType.Authentication or WebAuthnStepType.Registration
 * @param {Object} step the current SDK step
 * @returns {Promise} SDK WebAuthn promise resolved when WebAuthn is completed
 */
export function createWebAuthnCallbackPromise(type, step) {
  if (type === WebAuthnStepType.Authentication) {
    return FRWebAuthn.authenticate(step);
  }
  return FRWebAuthn.register(step);
}

/**
 * @description provides mappings for props and listeners associated with specific callbacks required for a UI showing callbacks
 * @param {String} componentType the type of component to provide props and listeners for
 * @param {Number} index the index of the current callback in the callback list
 * @param {Array} componentList the list of current callback components
 * @param {Object} currentStage the stage property for the current SDK step
 * @param {Object} currentStep the current SDK step
 * @param {String} realm the realm to validate password requirements against
 */
export function getComponentPropsAndEvents(componentType, callBackIndex, componentList, currentStage, currentStep, realm) {
  const existsInComponentList = (type) => componentList.find((component) => component.type === `Fr${type}`);
  const componentPropsAndEvents = {
    ChoiceCallback: () => {
      let stage;
      if (currentStage.ChoiceCallback) {
        stage = currentStage.ChoiceCallback.shift();
      }
      return { callbackSpecificProps: { stage } };
    },
    ConfirmationCallback: () => {
      let stage;
      if (currentStage.ConfirmationCallback) {
        stage = currentStage.ConfirmationCallback.shift();
      }
      return { callbackSpecificProps: { stage, variant: existsInComponentList(FrCallbackType.WebAuthnComponent) ? 'link' : 'primary' } };
    },
    ConsentMappingCallback: () => ({
      callbackSpecificProps: { callbacks: currentStep.callbacks },
      listeners: ['disable-next-button', 'did-consent'],
    }),
    HiddenValueCallback: () => ({
      listeners: ['hidden-value-callback-ref'],
    }),
    KbaCreateCallback: () => ({
      callbackSpecificProps: { showHeader: !existsInComponentList(FrCallbackType.KbaCreateCallback) },
      listeners: ['disable-next-button'],
    }),
    ReCaptchaCallback: () => ({
      listeners: ['next-step-callback'],
    }),
    SelectIdPCallback: () => ({
      callbackSpecificProps: { isOnlyCallback: currentStep.callbacks.length === 1 },
      listeners: ['hide-next-button', 'disable-next-button'],
    }),
    TextOutputCallback: () => ({
      listeners: ['disable-next-button', 'has-scripts', 'hide-next-button', 'next-step-callback'],
    }),
    ValidatedCreatePasswordCallback: () => {
      let stage;
      if (currentStage.ValidatedCreatePasswordCallback) {
        stage = currentStage.ValidatedCreatePasswordCallback.shift();
      }
      return {
        callbackSpecificProps: {
          callBackIndex,
          overrideInitialPolicies: true,
          realm,
          stage,
        },
        listeners: ['disable-next-button', 'next-step-callback', 'update-auth-id'],
      };
    },
    WebAuthnComponent: () => {
      const webAuthnType = FRWebAuthn.getWebAuthnStepType(currentStep);
      const webAuthnPromise = createWebAuthnCallbackPromise(webAuthnType, currentStep);
      return {
        callbackSpecificProps: { webAuthnType, webAuthnPromise },
      };
    },
  };
  return componentPropsAndEvents[componentType] ? componentPropsAndEvents[componentType]() : {};
}

export default {
  name: 'LoginMixin',
  mixins: [NotificationMixin],
  data() {
    return {
      FrCallbackType,
    };
  },
  methods: {
    logoutUser() {
      window.logout();
    },
    getIdFromSession,
    getUserInfo,
    getConfigurationInfo,
    isDefaultPath,
    isSamlURL,
    verifyGotoUrlAndRedirect,
    getCurrentQueryString,
    parseParameters,
    isCallbackRequired,
    getTranslatedPolicyFailures,
    getField,
    backendScriptsIdsContains,
    getComponentPropsAndEvents,
  },
};
</script>
